import * as R from 'ramda'
import * as React from 'react'
import * as ReactRedux from 'react-redux'
import * as ReactRouter from 'react-router-dom'
import PropTypes from 'prop-types'
import querystring from 'querystring'

import * as Api from '@rushplay/api-client'
import * as Analytics from '@rushplay/analytics'
import * as Common from '@rushplay/common'
import * as Forms from '@rushplay/forms'
import * as I18n from '@rushplay/i18n'
import * as Notifications from '@rushplay/notifications'
import * as Offers from '@rushplay/offers'
import styled from '@emotion/styled'

import * as Cookies from '../cookies-module'
import * as Constants from '../constants'
import * as Player from '../player'
import * as Session from '../session'
import * as Theming from '../theming'
import * as Utils from '../utils'
import { HtmlContent } from '../html-content'
import { InputField } from '../input-field'
import { OfferPicker } from '../offer-picker'
import { Payer } from '../payer'
import { QueryDrawer } from '../query-drawer'
import { SubmitButton } from '../submit-button'
import { useBriteDataSchema } from '../use-brite-data-schema'
import { useSafeUpdateQuery } from '../use-safe-update-query.js'

const Wrapper = styled.div`
  flex-grow: 1;
  padding-bottom: 16px;

  > form {
    height: 100%;
  }
`

function Amount(props) {
  const i18n = I18n.useI18n()
  const currency = Player.useCurrency()

  return (
    <div>
      <InputField
        currency={currency}
        initialValue={props.initialValue}
        inputMode="decimal"
        normalize={Utils.Amounts.normalizeAmount}
        parse={Utils.Amounts.parseAmount}
        scope="#/properties/amount"
      />

      <OfferPicker
        selectedOfferId={props.offerId}
        transactionType={props.transactionType}
        onChangeOfferId={offerId => props.onChangeOfferId(offerId)}
      />

      <Common.Box fontSize={1} textAlign="center" pt={2}>
        <HtmlContent
          html={{
            __html: i18n.translate('brite-login-form.terms-and-privacy'),
          }}
        />
      </Common.Box>

      <Common.Box display="flex" justifyContent="center" py={2}>
        <SubmitButton />
      </Common.Box>
    </div>
  )
}

Amount.propTypes = {
  currency: PropTypes.oneOf(Constants.CurrencyCodes),
  initialValue: PropTypes.number,
  offerId: PropTypes.number,
  transactionType: PropTypes.string,
  onChangeOfferId: PropTypes.func,
}

function Login(props) {
  const [step, setStep] = React.useState(
    props.flow === Constants.BriteLoginFlow.NORMAL ? 1 : 0
  )
  const [depositMethod, setDepositMethod] = React.useState('')
  const [offerId, setOfferId] = React.useState(null)
  const dispatch = ReactRedux.useDispatch()
  const dataSchema = useBriteDataSchema(props.flow)
  const history = ReactRouter.useHistory()
  const [, setOfferClaimed] = Cookies.useCookie(
    Constants.CookieKeys.OFFER_CLAIMED
  )
  const affiliateId = ReactRedux.useSelector(state =>
    Analytics.getAffiliateId(state.analytics)
  )
  const [returningPlayer] = Cookies.useCookie(
    Constants.CookieKeys.RETURNING_PLAYER
  )
  const isDeposit = props.flow === Constants.BriteLoginFlow.DEPOSIT
  const closeQuery = useSafeUpdateQuery({
    'login-flow': null,
    amount: null,
    login: null,
  })

  React.useEffect(() => {
    if (!returningPlayer) {
      dispatch(Offers.fetchDepositOffers({ affiliateId }))
    }
  }, [])

  const onSelectMethod = React.useCallback(
    method => {
      if (method && isDeposit) {
        dispatch(Analytics.selectPaymentMethod(method))
        setDepositMethod(method)
      }
    },
    [dispatch, isDeposit]
  )

  const onSuccess = React.useCallback(
    ({ sessionToken }) => {
      if (isDeposit && props.amount) {
        if (offerId) {
          setOfferClaimed(true, {
            httpOnly: false,
            maxAge: 365 * 24 * 60 * 60,
            path: '/',
          })

          dispatch(
            Api.claimDepositOffer(offerId, {
              failure: res => {
                Notifications.add({
                  level: 'error',
                  message: `errors.${res.value.message || 'general.unknown'}`,
                })
              },
              version: 2,
              token: sessionToken,
            })
          )
        }
      }

      dispatch([Session.fetch(sessionToken), Analytics.authenticate()])

      if (returningPlayer) {
        history.push(`?${closeQuery}`)
      } else {
        history.push('/casino')
      }

      // hack to avoid fetching player info before deposit count is updated on BE side
      const id = setTimeout(() => {
        if (isDeposit) {
          dispatch([
            Player.fetchPlayerInfo({
              success: () => [
                Analytics.completeTransaction('successful', depositMethod),
              ],
            }),
          ])
        }
      }, 15000)
      return () => {
        clearTimeout(id)
      }
    },
    [dispatch, isDeposit, depositMethod, props.amount, offerId, returningPlayer]
  )

  const onCancel = React.useCallback(() => {
    if (isDeposit) {
      dispatch(Analytics.completeTransaction('cancelled', depositMethod))
    }
    setOfferClaimed()
    history.push(`?${closeQuery}`)
  }, [dispatch, isDeposit, depositMethod])

  const onFailure = React.useCallback(
    (status, { errors }) => {
      if (isDeposit) {
        dispatch(Analytics.completeTransaction(status, depositMethod))
      }
      errors.length > 1 &&
        errors.forEach(error => {
          dispatch(
            Notifications.add({
              level: 'error',
              message: `errors.${error || 'general.unknown'}`,
            })
          )
        })
    },
    [dispatch, isDeposit, depositMethod]
  )

  return (
    <Wrapper>
      <Forms.Provider
        name="brite-login-form"
        schema={dataSchema}
        onSubmit={(errors, data) => {
          if (R.isEmpty(errors)) {
            if (props.flow === 'deposit') {
              dispatch(Analytics.selectAmount(data.amount))
              setStep(1)
            }
          }
        }}
      >
        <Theming.Alternative>
          <React.Fragment>
            {step === 0 && (
              <Amount
                initialValue={props.amount}
                offerId={offerId}
                transactionType={Constants.BriteLoginFlow.DEPOSIT}
                onChangeOfferId={data => setOfferId(data)}
              />
            )}
            {step === 1 && (
              <Payer
                transactionType={
                  props.flow === Constants.BriteLoginFlow.NORMAL
                    ? 'auth'
                    : 'deposit'
                }
                amountCents={props.flow === 'normal' ? null : props.amount}
                offerId={offerId}
                onSelectMethod={onSelectMethod}
                onFailure={onFailure}
                onSuccess={onSuccess}
                onCancel={onCancel}
              />
            )}
          </React.Fragment>
        </Theming.Alternative>
      </Forms.Provider>
    </Wrapper>
  )
}

Login.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(null)]),
  flow: PropTypes.oneOf(['normal', 'deposit']),
}

function BriteLoginDrawer() {
  const i18n = I18n.useI18n()
  const location = ReactRouter.useLocation()
  const query = querystring.parse(R.drop(1, location.search))
  const activeQueryName = 'login-flow'
  const amount =
    Number.parseInt(query.amount) || Utils.Amounts.getInitialAmount() || 0
  const loginFlow = query[activeQueryName]

  return (
    <QueryDrawer
      activeQueryName={activeQueryName}
      additionalQueries={{ amount: null }}
      title={i18n.translate('brite-login.title')}
    >
      {loginFlow && ['normal', 'deposit'].includes(loginFlow) && (
        <Login amount={amount} flow={loginFlow} />
      )}
    </QueryDrawer>
  )
}

// for @loadable/component
export default BriteLoginDrawer
